import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { MessageCircle, Check, X, Zap } from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/animations';
import LandingHeader from '@/components/layout/LandingHeader';
import IndustriesGrid from '@/components/info/IndustriesGrid';
import { HowItWorksMobile } from '@/components/info/HowItWorks';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import ProductsGrid from '@/components/info/ProductsGrid';
import { ContactForm, ContactInfoDetails } from '@/components/info/ContactForm';
import { CALLS_HANDLED, HOURS_SAVED } from '@/lib/constants';
import CustomAILogo from '@/components/CustomAILogo';
import { trackEvent } from '@/lib/capi';

const CONTACT_SECTION_ID = 'contact-section';

/* Exact rendered values of --c-bg-base / --c-bg-surface in dark mode. */
const FADE_BLACK = '#070D1B';
const FADE_CHARCOAL = '#0C1527';
const FADE_WHITE = '#FFFFFF';

/* Absolutely-positioned overlay painted at the top of a section, fading from
   the previous section's color into transparent. Adds no extra page height —
   it sits on top of the section's own top padding instead of between
   sections. Pair with `sectionContentZ` on the actual content wrapper so
   text always paints above it regardless of fade height. */
function FadeOverlay({ from, height = 140 }) {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 pointer-events-none"
      style={{ height, backgroundImage: `linear-gradient(to bottom, ${from}, transparent)` }}
    />
  );
}
const sectionContentZ = 'relative z-10';

const OFFER_POPUP_DELAY_MS = 10000;
const OFFER_POPUP_SESSION_KEY = 'landing-offer-popup-shown';

// `fbclid` is appended automatically by Meta to any link a visitor clicks from
// an ad, so its presence (or an ad-platform utm_source) is what distinguishes
// someone who actually arrived from the ad vs. someone who just typed /landing.
const AD_SOURCE_PARAMS = ['facebook', 'instagram', 'meta', 'fb'];
function isFromAdClick() {
  const params = new URLSearchParams(window.location.search);
  return params.has('fbclid') || AD_SOURCE_PARAMS.includes((params.get('utm_source') || '').toLowerCase());
}

const OFFER_POPUP_BENEFITS = [
  'A custom-trained AI walkthrough for your business',
  'No obligation, no pressure',
  'We reply within 24 hours',
];

const STATS = [
  { value: `${CALLS_HANDLED}+`, label: 'Calls Handled' },
  { value: `${HOURS_SAVED}+`, label: 'Hours Saved' },
  { value: '24/7', label: 'Coverage' },
  { value: '100%', label: 'Custom-Built' },
  { value: '2 minutes', label: 'Handled Per Call' },
  { value: '1 Day', label: 'A Week Saved in Admin Time' },
];

const CALL_OUTCOMES = [
  { key: 'voicemail', label: 'Voicemail', value: 50.5, color: '#3987e5' },
  { key: 'answered', label: 'Answered', value: 35.9, color: '#d95926' },
  { key: 'transfers', label: 'Transfers', value: 7.8, color: '#199e70' },
  { key: 'callbacks', label: 'Callbacks', value: 5.8, color: '#c98500' },
];

const COMPARISON_ROWS = [
  {
    label: 'AI training',
    custum: 'Your own custom-trained AI, built specifically for your business',
    others: 'Generic, templated AI running the same script for every client',
  },
  {
    label: 'Local context',
    custum: 'Australian context, including Australian voices',
    others: 'Scripts that don’t fit local customers',
  },
  {
    label: 'Data handling',
    custum: 'Security & privacy focused — your data stays yours',
    others: 'Data often pooled or reused across other customers',
  },
  {
    label: 'Ongoing support',
    custum: 'Active support & ongoing training and refinement',
    others: 'Set-and-forget with little to no ongoing support',
  },
  {
    label: 'Time to launch',
    custum: 'Setup in 1–2 weeks, reviewed again at 4 weeks',
    others: 'Long onboarding, or DIY setup left to you',
  },
  {
    label: 'System fit',
    custum: 'Connects with our other modules — one connected system',
    others: 'Disconnected point solutions across multiple vendors',
  },
];

// `location` identifies which section a click came from (there are several
// of these buttons down the page) and doubles as the data-testid so the
// tel:/mailto:/cta click listener already registered in public/index.html
// picks these up for GA too, on top of the Meta CTAClick event fired here.
function GetInTouchButton({ text = "Get in Touch", location = 'default' } = {}) {
  return (
    <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4 mb-2">
      <a
        href={`#${CONTACT_SECTION_ID}`}
        className="cta-button"
        style={{ border: '1px solid var(--brand-border)' }}
        data-testid={`landing-cta-${location}`}
        onClick={() => trackEvent('CTAClick', { eventParams: { cta_text: text, cta_location: location } })}
      >
        <MessageCircle size={18} /> {text}
      </a>
    </motion.div>
  )
}

function HeroSection() {
  const words1 = "From Missed Calls to Booked Jobs in Under a Month".split(' ');
  const words2 = "Without the Setup Hassle, Hiring Anyone, or Chasing Quotes Yourself".split(' ');
  const highlighted = ['Missed', 'Calls', 'Booked', 'Jobs', 'Month'];

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-brand-black">
      <div className="grain-overlay" />
      <div className="hero">
        <div className="hero-grid">
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="lg:col-span-7">
            <div className="heading">
              {words1.map((word, i) => (
                <motion.span key={i} custom={i} variants={{ hidden: { opacity: 0, y: 30 }, visible: (idx) => ({ opacity: 1, y: 0, transition: { delay: 0.2 + idx * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }) }} initial="hidden" animate="visible"
                  className={`text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight ${highlighted.includes(word) ? 'logo-gradient-text' : 'text-brand-white'}`}
                >{word}</motion.span>
              ))}
            </div>

            <div className="heading">
              {words2.map((word, i) => (
                <motion.span key={i} custom={i + words1.length} variants={{ hidden: { opacity: 0, y: 30 }, visible: (idx) => ({ opacity: 1, y: 0, transition: { delay: 0.2 + idx * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] } }) }} initial="hidden" animate="visible"
                  className="text-3xl sm:text-3xl lg:text-3xl font-extrabold tracking-tight text-brand-white"
                >{word}</motion.span>
              ))}
            </div>
            <motion.p variants={fadeInUp} className="text-m text-brand-white mb-4 text-center">
              Trained on your business knowledge. Your AI Receptionist answers every call 24/7, books appointments, captures job details so you can quote fast, and gets everything straight to you - set up and take calls within as little as 2 weeks.
            </motion.p>

            <GetInTouchButton text="Build your AI" location="hero"/>
            <motion.p variants={fadeInUp} className="text-xs text-brand-white mb-4 text-center">
              Free consultation · No obligation · We reply within 24 hours
            </motion.p>

            <motion.div variants={fadeInUp} className="relative -z-10 -mt-20 md:-mt-28 w-screen ml-[calc(50%-50vw)]">
              <img
                src="/img/tradie.webp"
                alt="Tradie checking a message handled by his AI receptionist"
                className="w-full h-auto select-none"
                style={{
                  maskImage:
                    'linear-gradient(to bottom, transparent 0%, black 62%, black 55%, transparent 100%)',
                  WebkitMaskImage:
                    'linear-gradient(to bottom, transparent 0%, black 62%, black 55%, transparent 100%)',
                  maskComposite: 'intersect',
                  WebkitMaskComposite: 'source-in',
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                }}
              />
            </motion.div>
          </motion.div>
          <div className="hidden lg:block lg:col-span-5" />
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  const loopStats = [...STATS, ...STATS];

  return (
    <section data-testid="stats-section" className="pt-32 md:pt-40 pb-20">
      <div className="relative overflow-hidden bg-gradient-to-r from-brand-blue-dark via-brand-blue to-brand-blue-dark p-8 md:p-12">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)' }} />
        </div>

        <div className="absolute inset-y-0 left-0 w-16 md:w-24 bg-gradient-to-r from-brand-blue-dark to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 md:w-24 bg-gradient-to-l from-brand-blue-dark to-transparent z-10 pointer-events-none" />

        <div className="flex w-max animate-marquee">
          {loopStats.map((stat, i) => (
            <div
              key={i}
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
              className="flex flex-col items-center gap-1 px-14 md:px-20 shrink-0"
            >
              <span className="text-3xl md:text-4xl font-extrabold text-white whitespace-nowrap">{stat.value}</span>
              <span className="text-sm text-blue-100/80 whitespace-nowrap">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CallStatsSection() {
  const [activeKey, setActiveKey] = useState(null);

  return (
    <section data-testid="call-stats-section" className="theme-light-section relative overflow-hidden pt-24 md:pt-32 pb-12 md:pb-16 bg-brand-black">
      <FadeOverlay from={FADE_BLACK} height={70} />
      <div className={`${sectionContentZ} max-w-4xl mx-auto px-6 md:px-12 lg:px-16`}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-16">
          <motion.h2 variants={fadeInUp} className="title">
            <span>Half</span> Your Calls Go To <span>Voicemail</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-xl mx-auto">
            Free up your team from time spent waiting for answers and leaving voicemails, so they can focus on leads who are ready to engage.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={fadeInUp}
          className="glass-card p-6 md:p-10"
        >
          <p className="text-sm font-semibold text-brand-off-white mb-6 text-center">What Happened To This Client's Calls</p>

          <div data-testid="call-outcomes-bar" className="flex w-full h-7 md:h-8 rounded-full overflow-hidden gap-[2px]">
            {CALL_OUTCOMES.map((seg) => (
              <Tooltip key={seg.key}>
                <TooltipTrigger asChild>
                  <div
                    data-testid={`call-outcome-${seg.key}`}
                    className="h-full flex items-center justify-center cursor-pointer transition-[opacity,filter] duration-200"
                    style={{
                      width: `${seg.value}%`,
                      backgroundColor: seg.color,
                      opacity: activeKey && activeKey !== seg.key ? 0.45 : 1,
                      filter: activeKey === seg.key ? 'brightness(1.15)' : 'none',
                    }}
                    onMouseEnter={() => setActiveKey(seg.key)}
                    onMouseLeave={() => setActiveKey(null)}
                  >
                    {seg.value >= 20 && (
                      <span className="text-xs md:text-sm font-semibold text-white whitespace-nowrap">
                        {seg.value}%
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-[220px] text-center bg-brand-blue border-brand-blue">
                  <p className="font-semibold text-white">{seg.label} — {seg.value}%</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function IndustrySection() {
  return (
    <section data-testid="industry-section" className="relative overflow-hidden pt-24 md:pt-32 pb-12 md:pb-16 bg-brand-black">
      <FadeOverlay from={FADE_WHITE} height={70} />
      <div className={`${sectionContentZ} max-w-7xl mx-auto px-6 md:px-12 lg:px-16`}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-16">
          <motion.p variants={fadeInUp} className="subtitle">Wondering if AI can work for your business?</motion.p>
          <motion.h2 variants={fadeInUp} className="title">
             <span>Chances Are</span> , It Already Works In Yours.
          </motion.h2>
        </motion.div>
        <IndustriesGrid />
        <div className="mt-8">
          <GetInTouchButton text="Check if it works for you" location="industry" />
        </div>
      </div>
    </section>
  )
}

function ProductsSection() {
  return (
    <section data-testid="products-section" className="theme-light-section relative overflow-hidden pt-12 md:pt-16 pb-24 md:pb-32 bg-brand-black">
      <FadeOverlay from={FADE_BLACK} height={60} />
      <div className={`solution ${sectionContentZ}`}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="solution-center">
          <motion.h2 variants={fadeInUp} className="title">
            One Platform. <span>Every Product You Need.</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="description">
            Every product is custom-built for your business and connected through automation. No one-size-fits-all.
          </motion.p>
        </motion.div>

        <ProductsGrid />
        <div className="mt-8">
          <GetInTouchButton text="Learn more" location="products" />
        </div>
      </div>
    </section>
  );
}



function ComparisonSection() {
  return (
    <section data-testid="comparison-section" className="relative overflow-hidden pt-20 md:pt-28 pb-3 md:pb-4 bg-brand-charcoal">
      <FadeOverlay from={FADE_CHARCOAL} height={40} />
      <div className={`${sectionContentZ} max-w-5xl mx-auto px-6 md:px-12 lg:px-16`}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-16">
          <motion.h2 variants={fadeInUp} className="title">
            How We Compare to <span>Other AI Providers</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-xl mx-auto">
            Not all AI is built the same. Here's what sets a custom-built system apart from generic, off-the-shelf AI.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={fadeInUp}
          className="glass-card overflow-hidden"
        >
          <div className="grid grid-cols-2">
            <div className="p-4 md:p-6 flex justify-center border-b" style={{ borderColor: 'rgba(15, 23, 42, 0.10)', background: 'rgba(var(--c-purple-rgb),0.1)' }}>
                <CustomAILogo />
            </div>
            <div className="p-4 md:p-6 text-center border-b border-l" style={{ borderColor: 'rgba(15, 23, 42, 0.10)' }}>
              <span className="text-sm md:text-base font-semibold text-brand-muted">Other AI Providers</span>
            </div>
          </div>

          {COMPARISON_ROWS.map((row, idx) => (
            <div key={row.label} className={`grid grid-cols-2 ${idx < COMPARISON_ROWS.length - 1 ? 'border-b' : ''}`} style={{ borderColor: 'rgba(15, 23, 42, 0.10)' }}>
              <div data-testid={`comparison-row-custum-${idx}`} className="p-4 md:p-6 flex items-start gap-3">
                <Check size={16} className="text-brand-blue-light mt-0.5 flex-shrink-0" />
                <span className="text-xs md:text-sm text-brand-off-white leading-relaxed">{row.custum}</span>
              </div>
              <div data-testid={`comparison-row-others-${idx}`} className="p-4 md:p-6 flex items-start gap-3 border-l" style={{ borderColor: 'rgba(15, 23, 42, 0.10)' }}>
                <X size={16} className="text-brand-muted mt-0.5 flex-shrink-0" />
                <span className="text-xs md:text-sm text-brand-muted leading-relaxed">{row.others}</span>
              </div>
            </div>
          ))}
        </motion.div>

        <div className="mt-12">
          <GetInTouchButton location="comparison" />
        </div>
      </div>
    </section>
  );
}

function OfferPopup() {
  const [open, setOpen] = useState(false);
  const primaryActionRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem(OFFER_POPUP_SESSION_KEY)) return undefined;
    const timer = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem(OFFER_POPUP_SESSION_KEY, '1');
    }, OFFER_POPUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-md border-brand-border bg-brand-charcoal text-center p-8 sm:p-10"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          primaryActionRef.current?.focus();
        }}
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="cta-badge">
            <Zap size={13} /> WAIT
          </div>
          <DialogTitle className="text-2xl sm:text-3xl font-bold text-brand-white tracking-tight">
            Try Your AI Agent Free for <span className="text-brand-blue">30 Days</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-muted">
            Give us 15 minutes and we&apos;ll show you exactly how an AI receptionist can start answering your calls and booking jobs for your business — with a full 30-day trial before you commit to anything.
          </DialogDescription>
        </DialogHeader>

        <ul className="cta-benefits">
          {OFFER_POPUP_BENEFITS.map((benefit) => (
            <li key={benefit}>
              <Check size={16} className="text-brand-blue-light flex-shrink-0" />
              <span>{benefit}</span>
            </li>
          ))}
        </ul>

        <a
          ref={primaryActionRef}
          href={`#${CONTACT_SECTION_ID}`}
          className="cta-button w-full justify-center"
          data-testid="landing-cta-offer_popup"
          onClick={() => {
            trackEvent('CTAClick', { eventParams: { cta_text: "Let's Talk!", cta_location: 'offer_popup' } });
            setOpen(false);
          }}
        >
          <MessageCircle size={18} /> Let's Talk!
        </a>

        <button
          type="button"
          onClick={() => setOpen(false)}
          className="border-0 bg-transparent p-0 text-xs text-brand-muted underline underline-offset-2 hover:text-brand-off-white"
        >
          No thanks, I&apos;ll figure it out myself
        </button>
      </DialogContent>
    </Dialog>
  );
}

const CTA_BENEFITS = [
  'Your own custom-trained AI, built specifically for your business',
  'Live and taking calls in as little as 1-2 weeks',
  'Free consultation, free trial, no obligation',
];

function CTASection() {
  return (
    <section data-testid="cta-section" className="py-24 md:py-32 bg-brand-charcoal relative overflow-hidden">
      <div className={`${sectionContentZ} max-w-2xl mx-auto px-6 md:px-12 lg:px-16`}>
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer}
          className="glass-card text-center p-8 sm:p-10 md:p-14"
        >
          <motion.h2 variants={fadeInUp} className="text-4xl sm:text-5xl font-bold text-brand-white tracking-tight mb-6">
            Try Your AI Agent Free for <span className="text-brand-blue">30 Days</span>
          </motion.h2>
          <motion.ul variants={fadeInUp} className="cta-benefits">
            {CTA_BENEFITS.map((benefit) => (
              <li key={benefit}>
                <Check size={16} className="text-brand-blue-light flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </motion.ul>
          <GetInTouchButton text="Let's Talk!" location="cta_section" />
          <motion.p variants={fadeInUp} className="text-xs text-brand-muted mt-4">
            30-day trial · No obligation · We reply within 24 hours
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section id={CONTACT_SECTION_ID} data-testid="contact-section" className="theme-light-section relative overflow-hidden py-24 md:py-32 bg-brand-black" style={{ scrollMarginTop: '4rem' }}>
      <FadeOverlay from={FADE_CHARCOAL} height={90} />
      <div className={`${sectionContentZ} max-w-6xl mx-auto px-6 md:px-12 lg:px-16`}>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={staggerContainer} className="text-center mb-16">
          <motion.p variants={fadeInUp} className="subtitle">GET IN TOUCH</motion.p>
          <motion.h2 variants={fadeInUp} className="title">
            Let&apos;s <span>Talk</span>
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-base text-brand-muted max-w-xl mx-auto">
            Book a free discovery call or send us a message. We&apos;ll get back to you within 24 hours.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={fadeInUp}
            className="lg:col-span-4"
          >
            <ContactInfoDetails />
          </motion.div>

          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-100px' }} variants={fadeInUp}
            className="lg:col-span-8"
          >
            <ContactForm formName="landing_page" successRedirect="/ai-receptionist-for-tradies/thank-you/" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  useEffect(() => {
    if (isFromAdClick()) {
      trackEvent('ViewContent', { eventParams: { content_name: 'landing_page', content_category: 'paid_ad' } });
    }
  }, []);

  return (
    <TooltipProvider delayDuration={150}>
      {/* Always dark regardless of the visitor's site-wide theme preference */}
      <div data-testid="landing-page">
        <Helmet>
          <title>AI Receptionist for Tradies | Never Miss a Call – Custum AI</title>
          <meta name="description" content="An AI receptionist trained on your business. Answers every call 24/7, books jobs and captures quote details for Australian tradies." />
          <meta name="robots" content="index, follow" />
          <meta property="og:title" content="AI Receptionist for Tradies | Never Miss a Call – Custum AI" />
          <meta property="og:description" content="An AI receptionist trained on your business. Answers every call 24/7, books jobs and captures quote details for Australian tradies." />
          <meta property="og:type" content="website" />
          <link rel="canonical" href="https://custum.ai/ai-receptionist-for-tradies/" />
        </Helmet>
        <OfferPopup />
        <LandingHeader />
        <HeroSection />
        <StatsSection />
        <CallStatsSection />
        <IndustrySection />
        <ProductsSection />
        <div className="relative bg-brand-charcoal flow-root">
          <FadeOverlay from={FADE_WHITE} height={80}/>
          <HowItWorksMobile />
          <GetInTouchButton text="See How It Works" location="how_it_works" />
        </div>
        <ComparisonSection />
        <CTASection />
        <ContactSection />
      </div>
    </TooltipProvider>
  );
}
